import React from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { Controller, DefaultValues, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import { CreateNoteInput } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import { notesAndObservationQuery } from "../NotesAndObservation";

type AddNoteModalType = {
  visible: boolean | undefined;
  onClose: () => void;
  onAddNote: () => Promise<unknown>;
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  modaltext: {
    backgroundColor: colors.white,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 4),
    width: "30%",
    maxHeight: "100%",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  buttonContainer: {
    display: "flex",
    marginTop: "40px",
    marginBottom: "30px",
    alignSelf: "center",
    justifyContent: "center",
  },
}));

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#FF6262",
    border: "2px solid #FF6262",
    marginRight: 8,
  },
});

const validationSchema = yup
  .object()
  .shape({
    date: yup.date().typeError("Please enter a valid date"),
    text: yup.string().required("Please enter your notes"),
  })
  .required();

type FormValues = yup.InferType<typeof validationSchema>;

const createNoteMutation = graphql(`
  mutation CreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      note {
        ...NoteItem
      }
    }
  }
`);

const AddNoteModal: React.FC<AddNoteModalType> = ({
  visible,
  onClose,
  onAddNote,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const { clientId } = useParams<{ clientId: string }>();
  const [createNote] = useMutation(createNoteMutation);
  const modalVisibility = visible !== undefined ? visible : false;
  const initialValues: DefaultValues<FormValues> = {
    date: new Date(),
    text: "",
  };
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values: FormValues) => {
    const date = values.date ?? new Date();
    const createNoteInput: CreateNoteInput = {
      attributes: {
        clientId: clientId,
        attachedTo: "profile",
        date: format(new Date(date), "yyyy-MM-dd"),
        text: values.text,
      },
    };
    try {
      await createNote({
        variables: { input: createNoteInput },
        refetchQueries: [
          {
            query: notesAndObservationQuery,
            variables: { id: clientId ?? "" },
          },
        ],
      });
      snackbar.open("Successfully added note");
      onClose();
      onAddNote();
      reset(initialValues);
    } catch (e) {
      snackbar.openSnackBar(
        displayError(
          e,
          "An error occured while adding a note. Please try again later."
        )
      );
    }
  };

  return (
    <Modal open={modalVisibility} onClose={onClose} className={classes.root}>
      <Box className={classes.modaltext}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>Add Note</Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box className={classes.fields}>
              <Typography variant="subtitle1" color="textPrimary">
                Date
              </Typography>
              <Controller
                control={control}
                name="date"
                render={({
                  field: { ref, name, onChange, ...field },
                  fieldState,
                }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    onChange={(value) => onChange(value as Date)}
                    slotProps={{
                      textField: {
                        name,
                        fullWidth: true,
                        error: !!fieldState?.error,
                        helperText: fieldState?.error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box className={classes.fields}>
              <Typography variant="subtitle1" color="textPrimary">
                Notes
              </Typography>
              <TextField
                {...register("text")}
                error={!!errors.text}
                helperText={errors.text?.message}
                fullWidth
                multiline
                rows={4}
              />
            </Box>

            <Box className={classes.buttonContainer}>
              <StyledCancelButton variant="outlined" onClick={onClose}>
                Cancel
              </StyledCancelButton>
              <Button type="submit" disabled={isSubmitting}>
                Add
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNoteModal;
